'use client';

import { useTranslation } from 'react-i18next';
import { useMediaSource, type MediaItem } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { SectionShell } from '../_shared/SectionShell';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { EmptyState } from '../_shared/EmptyState';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { ContentRail } from '../ui/ContentRail';
import { VideoCard } from '../system/VideoCard';

/**
 * Horizontal video rail. Fetches a source slug from
 * {@code MediaSource.videos.fetchSource} (e.g. {@code trending},
 * {@code popular}, {@code editor-picks}, {@code latest},
 * {@code new-this-week}).
 */
export interface VideoRowProps {
  title?: string;
  source?: string;
  limit?: number;
  seeAllLabel?: string;
  seeAllHref?: string;
  videoHrefTemplate?: string;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function VideoRow({
  title,
  source: rawSource,
  limit: rawLimit,
  seeAllLabel,
  seeAllHref,
  videoHrefTemplate = '/tube/{slug}',
}: VideoRowProps) {
  const { t } = useTranslation('site-builder-blocks');
  const media = useMediaSource();
  const source = (rawSource ?? '').trim() || 'trending';
  const limit = clamp(
    typeof rawLimit === 'number' && Number.isFinite(rawLimit) ? rawLimit : 8,
    1,
    24,
  );

  const state = useAsyncResource<{ items: MediaItem[]; label?: string }>(
    async (signal) => {
      if (!media) return null;
      const res = await media.videos.fetchSource({ source, limit, signal });
      return res && res.items.length > 0 ? res : null;
    },
    [media, source, limit],
  );

  const label =
    (typeof title === 'string' && title.trim()) ||
    (state.kind === 'loaded' ? state.data.label : undefined) ||
    source;

  const action = seeAllHref ? (
    <a
      href={seeAllHref}
      style={{
        fontSize: '13px',
        fontWeight: 600,
        color: 'var(--color-text-brand, var(--color-brand-600, #3b5bdb))',
        textDecoration: 'none',
      }}
    >
      {seeAllLabel || t('shared.seeAll')} →
    </a>
  ) : undefined;

  if (!media) {
    return (
      <SectionShell title={label} action={action}>
        <AdapterPlaceholder blockType="VideoRow" adapterName="MediaSource" />
      </SectionShell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <SectionShell title={label} action={action}>
        <SkeletonRow count={limit} layout="rail" />
      </SectionShell>
    );
  }

  if (state.kind !== 'loaded') {
    return (
      <SectionShell title={label} action={action}>
        <EmptyState
          title={t('shared.empty.videos.title')}
          hint={t('shared.empty.videos.hint')}
          diagnostic={source}
        />
      </SectionShell>
    );
  }

  return (
    <SectionShell title={label} action={action}>
      <ContentRail ariaLabel={label} snap="start">
        {state.data.items.map((item) => (
          <div key={item.id} style={{ flex: '0 0 280px', minWidth: 0 }}>
            <VideoCard item={item} variant="youtube" hrefTemplate={videoHrefTemplate} />
          </div>
        ))}
      </ContentRail>
    </SectionShell>
  );
}
